<script setup lang="ts">
import { debounce } from 'lodash-es';
import {
  computed,
  ref,
  toRef,
} from 'vue';

import type { IAffiliateProgramCreativeSchema } from '@modules/affiliate-trackers/submodules/creatives/types';
import type {
  FormFlattenKey,
  FormFlattenValue,
  FormSchema,
} from '@modules/forms/components/VForm/types';
import type { IFormSelectInputOption } from '@modules/forms/types';
import VButton from '@components/buttons/VButton/VButton.vue';
import VIconButton from '@components/buttons/VIconButton/VIconButton.vue';
import VDialog from '@components/dialogs/VDialog/VDialog.vue';
import { TRANSLATION_GROUP_AFFILIATE_CREATIVES } from '@modules/affiliate-trackers/submodules/creatives/constants';
import { useAffiliateTrackersCreativeLinkQuery } from '@modules/affiliate-trackers/submodules/creatives/queries';
import { useCopyToClipboard } from '@modules/core/composables/copy';
import VForm from '@modules/forms/components/VForm/VForm.vue';
import { FORM_INPUT_DEBOUNCE_DEFAULT_MS } from '@modules/forms/constants';
import { EFormInputTypes } from '@modules/forms/enums';
import { useI18n } from '@modules/i18n/composables';
import {
  assert,
  isOptionalString,
} from '@utils/guards';
import { watchImmediate } from '@vueuse/core';

import type { AffiliateCreativesCopyButtonProps } from './types';

const props = defineProps<AffiliateCreativesCopyButtonProps>();

const { getTranslation } = useI18n();

const isVisibleForm = ref(false);
const bannerCode = ref('');
const selectedOption = ref('');

const {
  data,
} = useAffiliateTrackersCreativeLinkQuery(
  toRef(props, 'creativeId'),
  toRef(props, 'trackerId'),
  bannerCode,
  isVisibleForm,
);

const formatOptions = computed<IFormSelectInputOption<string>[]>(() => [
  {
    label: getTranslation(TRANSLATION_GROUP_AFFILIATE_CREATIVES, 'MODAL_INTEGRATION_URL').value,
    value: 'url',
  },
  {
    label: getTranslation(TRANSLATION_GROUP_AFFILIATE_CREATIVES, 'MODAL_INTEGRATION_URL_JS').value,
    value: 'url_js',
  },
]);

const availableOptions = computed<string[]>(() => (
  Object.keys(data.value?.click_url ?? {})
));

watchImmediate(availableOptions, (newValue) => {
  if (!newValue.includes(selectedOption.value)) {
    selectedOption.value = newValue[0];
  }
});

const formSchema = computed<FormSchema<IAffiliateProgramCreativeSchema>>(() => {
  const schema: FormSchema<IAffiliateProgramCreativeSchema> = {
    code: {
      type: EFormInputTypes.TEXTAREA,
      readonly: true,
      label: getTranslation(TRANSLATION_GROUP_AFFILIATE_CREATIVES, 'MODAL_BANNER_CODE').value,
      isFullWidth: true,
      value: data.value?.click_url[selectedOption.value],
    },
    utmCode: {
      type: EFormInputTypes.TEXT,
      label: getTranslation(TRANSLATION_GROUP_AFFILIATE_CREATIVES, 'MODAL_ZONE_ID').value,
      isFullWidth: true,
      value: bannerCode.value,
    },
  };

  if (availableOptions.value.length > 1) {
    schema.format = {
      type: EFormInputTypes.SELECT,
      options: formatOptions.value,
      label: getTranslation(TRANSLATION_GROUP_AFFILIATE_CREATIVES, 'MODAL_INTEGRATION_LABEL').value,
      isFullWidth: true,
      value: selectedOption.value,
    };
  }

  return schema;
});

function showModal(): void {
  isVisibleForm.value = true;
}

function onModalHide(): void {
  isVisibleForm.value = false;
  bannerCode.value = '';
}

const { copyToClipboard } = useCopyToClipboard();

function onCopy(): void {
  const value = data.value?.click_url[selectedOption.value];
  if (value) {
    void copyToClipboard(value);
  }
}

function onChangeUtmCode(value: string): void {
  bannerCode.value = value ?? '';
}

const debouncedUpdate = debounce(onChangeUtmCode, FORM_INPUT_DEBOUNCE_DEFAULT_MS);

function onInteracted<
  T extends IAffiliateProgramCreativeSchema,
  K extends FormFlattenKey<T>,
>(
  key: K,
  value: FormFlattenValue<T, K> | undefined,
): void {
  assert(isOptionalString(value));

  if (key === 'format' && value) {
    selectedOption.value = value;
  }

  if (key === 'utmCode') {
    debouncedUpdate(value ?? '');
  }
}
</script>

<template>
  <div v-data-qa-auto-id="'AffiliateCreativesCopyButton'">
    <VIconButton
      icon="pi-copy"
      @click="showModal"
    />
    <VDialog
      :title="title || $getTranslation(TRANSLATION_GROUP_AFFILIATE_CREATIVES, 'MODAL_TITLE')"
      :visible="isVisibleForm"
      @hide="onModalHide"
    >
      <VForm
        :schema="formSchema"
        :groups="[
          {
            items: [
              'format',
              'code',
              'utmCode',
            ],
          },
        ]"
        @interacted="onInteracted"
      />
      <template #footer>
        <VButton
          :label="$getTranslation(TRANSLATION_GROUP_AFFILIATE_CREATIVES, 'MODAL_CANCEL')"
          outlined
          @click="onModalHide"
        />
        <VButton
          :label="$getTranslation(TRANSLATION_GROUP_AFFILIATE_CREATIVES, 'MODAL_COPY')"
          @click="onCopy"
        />
      </template>
    </VDialog>
  </div>
</template>
